import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  updateDoc, 
  doc, 
  getDoc, 
  setDoc,
  increment,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './config';

// Constants
const WAITLIST_COLLECTION = 'waitlist';
const BETA_STATS_DOC = 'beta_stats';
const BETA_STATS_COLLECTION = 'app_stats';

// Initialize beta stats if they don't exist
export const initializeBetaStats = async () => {
  const statsRef = doc(db, BETA_STATS_COLLECTION, BETA_STATS_DOC);
  const statsDoc = await getDoc(statsRef);
  
  if (!statsDoc.exists()) {
    await setDoc(statsRef, {
      totalSpots: 100,
      spotsRemaining: 100,
      registrationCloses: new Date('2025-06-15T23:59:59'),
      lastUpdated: serverTimestamp()
    });
  }
  
  return statsDoc.exists() ? statsDoc.data() : null;
};

// Get current beta stats
export const getBetaStats = async () => {
  const statsRef = doc(db, BETA_STATS_COLLECTION, BETA_STATS_DOC);
  const statsDoc = await getDoc(statsRef);
  
  if (!statsDoc.exists()) {
    return await initializeBetaStats();
  }
  
  return statsDoc.data();
};

// Add new waitlist entry
export const addWaitlistEntry = async (formData) => {
  try {
    // Check if email already exists
    const emailExists = await findWaitlistEntry(formData.email);
    if (emailExists) {
      throw new Error('This email is already registered for the beta program');
    }
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, WAITLIST_COLLECTION), {
      ...formData,
      createdAt: serverTimestamp(),
      status: 'pending' // For tracking status (pending, contacted, onboarded, etc.)
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding waitlist entry:', error);
    throw error;
  }
};

// Update spots remaining when new registration is approved
export const decrementSpotsRemaining = async () => {
  const statsRef = doc(db, BETA_STATS_COLLECTION, BETA_STATS_DOC);
  
  await updateDoc(statsRef, {
    spotsRemaining: increment(-1),
    lastUpdated: serverTimestamp()
  });
};

// Get all waitlist entries
export const getAllWaitlistEntries = async () => {
  const waitlistRef = collection(db, WAITLIST_COLLECTION);
  const q = query(waitlistRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get waitlist entries by status
export const getWaitlistEntriesByStatus = async (status) => {
  const waitlistRef = collection(db, WAITLIST_COLLECTION);
  const q = query(
    waitlistRef, 
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update waitlist entry status
export const updateWaitlistStatus = async (entryId, newStatus, notes = '') => {
  const entryRef = doc(db, WAITLIST_COLLECTION, entryId);
  
  await updateDoc(entryRef, {
    status: newStatus,
    statusUpdatedAt: serverTimestamp(),
    notes: notes
  });
  
  // If approving a beta tester, decrement spots remaining
  if (newStatus === 'approved') {
    await decrementSpotsRemaining();
  }
};

// Get waitlist entry by email or phone
export const findWaitlistEntry = async (searchValue) => {
  // Try to find by email
  let q = query(
    collection(db, WAITLIST_COLLECTION),
    where('email', '==', searchValue)
  );
  let snapshot = await getDocs(q);
  
  if (!snapshot.empty) {
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))[0];
  }
  
  // Try to find by phone
  q = query(
    collection(db, WAITLIST_COLLECTION),
    where('phoneNumber', '==', searchValue)
  );
  snapshot = await getDocs(q);
  
  if (!snapshot.empty) {
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))[0];
  }
  
  return null;
};

// Get recent waitlist entries (for dashboard)
export const getRecentWaitlistEntries = async (count = 5) => {
  const waitlistRef = collection(db, WAITLIST_COLLECTION);
  const q = query(
    waitlistRef,
    orderBy('createdAt', 'desc'),
    limit(count)
  );
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Export all functions
export default {
  initializeBetaStats,
  getBetaStats,
  decrementSpotsRemaining,
  addWaitlistEntry,
  getAllWaitlistEntries,
  getWaitlistEntriesByStatus,
  updateWaitlistStatus,
  findWaitlistEntry,
  getRecentWaitlistEntries
};
